import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, Copy, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const Inviti = () => {
  const [invites, setInvites] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = () => api.get("/admin/invites").then(({ data }) => setInvites(data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const generate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { note });
      toast.success(`Codice creato: ${data.code}`);
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Copia non riuscita: seleziona il codice manualmente.");
    }
  };

  return (
    <main className="mx-auto max-w-4xl px-4 py-14 sm:px-8 sm:py-20">
      <p className="eyebrow flex items-center gap-3">
        <span className="inline-block h-px w-8 bg-[#B85328]" />
        Area fondatori
      </p>
      <h1 data-testid="invites-heading" className="font-display mt-4 flex items-center gap-3 text-3xl font-semibold text-[#2C2723] sm:text-4xl lg:text-5xl">
        <Ticket className="text-[#B85328]" size={36} />
        Codici <span className="italic text-[#B85328]">invito</span>
      </h1>
      <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#8C7B6E]">
        Ogni codice vale per una sola iscrizione. Generane uno per ogni famiglia che ha adottato un
        cucciolo e inviaglielo su WhatsApp.
      </p>

      <form data-testid="invite-generate-form" onSubmit={generate} className="mt-10 flex flex-col gap-3 rounded-3xl border border-[#E8DFD5] bg-white p-6 sm:flex-row sm:items-center">
        <input
          data-testid="invite-note-input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Per chi è? Es. Famiglia Bianchi (Olmo)"
          className="flex-1 rounded-xl border border-[#E8DFD5] bg-[#FBF8F3] px-4 py-3.5 text-sm text-[#2C2723] outline-none transition-all placeholder:text-[#8C7B6E]/60 focus:border-[#B85328] focus:ring-2 focus:ring-[#B85328]/15"
        />
        <button
          type="submit"
          data-testid="invite-generate-button"
          disabled={busy}
          className="flex items-center justify-center gap-2 rounded-full bg-[#B85328] px-6 py-3.5 text-sm font-semibold text-[#FBF8F3] transition-all hover:bg-[#9C421C] disabled:opacity-60"
        >
          <Plus size={15} /> Genera codice
        </button>
      </form>

      <div data-testid="invites-list" className="mt-8 space-y-3">
        {invites.length === 0 && (
          <p data-testid="invites-empty" className="rounded-3xl border border-dashed border-[#CFC0B2] py-16 text-center font-display text-2xl italic text-[#5C534B]">
            Nessun codice ancora creato.
          </p>
        )}
        {invites.map((inv) => (
          <div
            key={inv.code}
            data-testid={`invite-row-${inv.code}`}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#E8DFD5] bg-white px-5 py-4"
          >
            <code className="rounded-lg bg-[#F5EFE6] px-3 py-1.5 font-mono text-sm font-semibold tracking-wider text-[#2C2723]">
              {inv.code}
            </code>
            <button
              data-testid={`invite-copy-${inv.code}`}
              onClick={() => copy(inv.code)}
              className="flex items-center gap-1.5 rounded-full border border-[#CFC0B2] px-3 py-1.5 text-xs font-semibold text-[#5C534B] transition-all hover:border-[#B85328] hover:text-[#B85328]"
            >
              {copied === inv.code ? <Check size={13} /> : <Copy size={13} />}
              {copied === inv.code ? "Copiato!" : "Copia"}
            </button>
            {inv.note && <span className="text-sm text-[#5C534B]">{inv.note}</span>}
            <span
              data-testid={`invite-status-${inv.code}`}
              className={`ml-auto rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${
                inv.used_by ? "bg-[#1F1A17]/85 text-[#D4A359]" : "bg-[#4A6B5D] text-white"
              }`}
            >
              {inv.used_by ? `Usato${inv.used_by_name ? ` da ${inv.used_by_name}` : ""}` : "Disponibile"}
            </span>
          </div>
        ))}
      </div>
    </main>
  );
};

export default Inviti;
